import {
  IAppointment,
  ITreatmentStep,
  PatientCollection,
  type IBrand,
  type IInvoice,
  type IOrganisation,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  collectionGroupQuery,
  orderBy,
  toTimestamp,
  where,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { BigQueryJobName } from '../../job-names';
import { ITreatmentStepWriteTopicData } from '../../topic-data/topic-data';
import {
  BasicBigQuerySyncHandler,
  IBigQuerySyncRequest,
} from '../big-query-sync';

export class BigQueryTreatmentStepSyncHandler extends BasicBigQuerySyncHandler<
  IAppointment,
  ITreatmentStepWriteTopicData
> {
  readonly bigQueryJobName = BigQueryJobName.TreatmentStepWrite;

  getQuery(request: IBigQuerySyncRequest): Query<IAppointment> {
    return collectionGroupQuery<IAppointment>(
      PatientCollection.Appointments,
      where('event.practice.ref', '==', request.practiceRef),
      where('event.from', '>=', request.range.from),
      where('event.from', '<=', request.range.to),
      orderBy('event.from')
    );
  }

  async getTopicData(
    appointment: WithRef<IAppointment>
  ): Promise<ITreatmentStepWriteTopicData> {
    const treatmentStep = await Firestore.getDoc(
      appointment.treatmentPlan.treatmentStep.ref
    );
    return this.getTreatmentStepTopicData(treatmentStep);
  }

  async getTreatmentStepTopicData(
    treatmentStep: WithRef<ITreatmentStep>
  ): Promise<ITreatmentStepWriteTopicData> {
    const invoiceRef = Firestore.getParentDocRef<IInvoice>(treatmentStep.ref);
    const patientRef = Firestore.getParentDocRef<IPatient>(invoiceRef);
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);

    return {
      organisation: await Firestore.getDoc(organisationRef),
      brand: await Firestore.getDoc(brandRef),
      patient: await Firestore.getDoc<IPatient>(patientRef),
      treatmentStep,
      timestamp: toTimestamp(),
    };
  }
}
